import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  UserGroupIcon,
  CurrencyEuroIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { adminService, DashboardStats as APIDashboardStats, RecentBooking as APIRecentBooking } from '../../services/adminService';
import './AdminDashboard.css';

interface DashboardStats {
  todaysBookings: number;
  pendingBookings: number;
  totalRevenue: number;
  monthlyRevenue: number;
  activeCustomers: number;
  upcomingEvents: number;
  pendingPayments: number;
  completedBookings: number;
}

interface RecentBooking {
  id: string;
  customerName: string;
  eventDate: string;
  participants: number;
  status: 'pending' | 'confirmed' | 'cancelled';
  totalAmount: number;
}

interface SystemAlert {
  id: number;
  type: string;
  message: string;
  severity: 'info' | 'warning' | 'error';
  timestamp: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    todaysBookings: 0,
    pendingBookings: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    activeCustomers: 0,
    upcomingEvents: 0,
    pendingPayments: 0,
    completedBookings: 0
  });
  
  const [recentBookings, setRecentBookings] = useState<RecentBooking[]>([]);
  const [systemAlerts, setSystemAlerts] = useState<SystemAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch real data from API
      const dashboardData = await adminService.getDashboardStats();
      const systemStatus = await adminService.getSystemStatus();
      
      // Transform API data to component state
      setStats({
        todaysBookings: dashboardData.today.newBookings || 0,
        pendingBookings: dashboardData.upcomingTrips?.filter(t => t.currentParticipants < 30).length || 0,
        totalRevenue: dashboardData.today.revenue || 0,
        monthlyRevenue: dashboardData.thisMonth.revenue || 0,
        activeCustomers: dashboardData.today.newCustomers || 0,
        upcomingEvents: dashboardData.thisWeek.scheduledTrips || 0,
        pendingPayments: 0, // TODO: Implement from backend
        completedBookings: dashboardData.thisWeek.newBookings || 0
      });

      // Transform recent bookings
      const transformedBookings: RecentBooking[] = dashboardData.recentBookings.map(booking => ({
        id: booking.id.toString(),
        customerName: booking.customerName,
        eventDate: new Date(booking.tripDate).toLocaleDateString('de-DE'),
        participants: booking.participantCount,
        status: 'confirmed' as const, // Default status since API doesn't provide it directly
        totalAmount: booking.totalAmount
      }));
      setRecentBookings(transformedBookings);

      // Transform system alerts
      const transformedAlerts: SystemAlert[] = dashboardData.systemAlerts.map(alert => ({
        id: alert.id,
        type: alert.alertType,
        message: alert.message,
        severity: alert.severity.toLowerCase() as 'info' | 'warning' | 'error',
        timestamp: alert.createdAt
      }));
      setSystemAlerts(transformedAlerts);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Fehler beim Laden der Dashboard-Daten');
      
      // Fallback to mock data if API fails
      setStats({
        todaysBookings: 5,
        pendingBookings: 12,
        totalRevenue: 45670,
        monthlyRevenue: 8450,
        activeCustomers: 234,
        upcomingEvents: 18,
        pendingPayments: 7,
        completedBookings: 156
      });

      setRecentBookings([
        {
          id: '1',
          customerName: 'Max Mustermann',
          eventDate: '20.09.2025',
          participants: 45,
          status: 'confirmed',
          totalAmount: 2850
        },
        {
          id: '2',
          customerName: 'Firma ABC GmbH',
          eventDate: '22.09.2025',
          participants: 30,
          status: 'pending',
          totalAmount: 1950
        },
        {
          id: '3',
          customerName: 'Sarah Schmidt',
          eventDate: '25.09.2025',
          participants: 60,
          status: 'confirmed',
          totalAmount: 3600
        }
      ]);
      
      setSystemAlerts([
        {
          id: 1,
          type: 'booking',
          message: 'Neue Buchung eingegangen',
          severity: 'info',
          timestamp: new Date().toISOString()
        },
        {
          id: 2,
          type: 'payment',
          message: 'Zahlung ausstehend für Buchung #234',
          severity: 'warning',
          timestamp: new Date().toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'badge-warning',
      confirmed: 'badge-success',
      cancelled: 'badge-danger'
    };
    return badges[status as keyof typeof badges] || 'badge-secondary';
  };

  const statsCards = [
    {
      title: 'Heutige Buchungen',
      value: stats.todaysBookings,
      icon: CalendarIcon,
      color: 'blue',
      link: '/admin/bookings?filter=today'
    },
    {
      title: 'Offene Buchungen',
      value: stats.pendingBookings,
      icon: ClockIcon,
      color: 'yellow',
      link: '/admin/bookings?filter=pending'
    },
    {
      title: 'Monatsumsatz',
      value: formatCurrency(stats.monthlyRevenue),
      icon: CurrencyEuroIcon,
      color: 'green',
      link: '/admin/reports'
    },
    {
      title: 'Aktive Kunden',
      value: stats.activeCustomers,
      icon: UsersIcon,
      color: 'purple',
      link: '/admin/customers'
    }
  ];

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Dashboard wird geladen...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <p className="dashboard-subtitle">Willkommen zurück! Hier ist Ihre Übersicht.</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {statsCards.map((card, index) => (
          <div
            key={index}
            className={`stat-card stat-card-${card.color}`}
            onClick={() => navigate(card.link)}
          >
            <div className="stat-icon">
              <card.icon className="stat-card-icon" />
            </div>
            <div className="stat-content">
              <h3>{card.title}</h3>
              <p className="stat-value">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Schnellzugriff</h2>
        <div className="action-buttons">
          <button
            className="action-btn btn-primary"
            onClick={() => navigate('/admin/booking-new')}
          >
            <CalendarIcon className="action-icon" />
            Neue Buchung
          </button>
          <button
            className="action-btn btn-yellow"
            onClick={() => navigate('/admin/customers', { state: { openDialog: true } })}
          >
            <UserGroupIcon className="action-icon" />
            Neuer Kunde
          </button>
          <button
            className="action-btn btn-info"
            onClick={() => navigate('/admin/invoices')}
          >
            <DocumentTextIcon className="action-icon" />
            Rechnungen
          </button>
          <button
            className="action-btn btn-success"
            onClick={() => navigate('/admin/reports')}
          >
            <ChartBarIcon className="action-icon" />
            Berichte
          </button>
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="recent-section">
        <div className="section-header">
          <h2>Aktuelle Buchungen</h2>
          <button
            className="btn-link"
            onClick={() => navigate('/admin/bookings')}
          >
            Alle anzeigen →
          </button>
        </div>
        
        <div className="bookings-table">
          <table>
            <thead>
              <tr>
                <th>Kunde</th>
                <th>Datum</th>
                <th>Teilnehmer</th>
                <th>Status</th>
                <th>Betrag</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {recentBookings.map((booking) => (
                <tr key={booking.id}>
                  <td>{booking.customerName}</td>
                  <td>{new Date(booking.eventDate).toLocaleDateString('de-DE')}</td>
                  <td>{booking.participants}</td>
                  <td>
                    <span className={`badge ${getStatusBadge(booking.status)}`}>
                      {booking.status === 'pending' ? 'Ausstehend' :
                       booking.status === 'confirmed' ? 'Bestätigt' : 'Storniert'}
                    </span>
                  </td>
                  <td>{formatCurrency(booking.totalAmount)}</td>
                  <td>
                    <button
                      className="btn-small btn-view"
                      onClick={() => navigate(`/admin/bookings/${booking.id}`)}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="additional-stats">
        <div className="stat-box">
          <CheckCircleIcon className="additional-stat-icon text-green-500" />
          <div>
            <h3>Abgeschlossene Buchungen</h3>
            <p className="stat-number">{stats.completedBookings}</p>
          </div>
        </div>
        <div className="stat-box">
          <ExclamationCircleIcon className="additional-stat-icon text-yellow-500" />
          <div>
            <h3>Offene Zahlungen</h3>
            <p className="stat-number">{stats.pendingPayments}</p>
          </div>
        </div>
        <div className="stat-box">
          <CalendarIcon className="additional-stat-icon text-blue-500" />
          <div>
            <h3>Kommende Events</h3>
            <p className="stat-number">{stats.upcomingEvents}</p>
          </div>
        </div>
        <div className="stat-box">
          <CurrencyEuroIcon className="additional-stat-icon text-green-500" />
          <div>
            <h3>Gesamtumsatz</h3>
            <p className="stat-number">{formatCurrency(stats.totalRevenue)}</p>
          </div>
        </div>
      </div>

      {/* System Alerts */}
      {systemAlerts.length > 0 && (
        <div className="alerts-section">
          <div className="section-header">
            <h2>System-Benachrichtigungen</h2>
          </div>
          <div className="alerts-list">
            {systemAlerts.map((alert) => (
              <div key={alert.id} className={`alert alert-${alert.severity}`}>
                <div className="alert-content">
                  <span className="alert-type">{alert.type}</span>
                  <span className="alert-message">{alert.message}</span>
                </div>
                <span className="alert-time">
                  {new Date(alert.timestamp).toLocaleString('de-DE')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <ExclamationCircleIcon className="error-icon" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;